/** Shared, declarative signature model keeps saved templates free from executable HTML. */
export const EMPTY_TEMPLATE = {
    version: 1,
    variables: [{ key: 'name', label: 'Name', placeholder: 'Alex Smith' }],
    rows: [{ id: 'row-1', layout: 'single', columns: [{ id: 'column-1', blocks: [{ id: 'block-1', type: 'text', value: '{{name}}', link: '', fontSize: 18, color: '#706f6f', bold: true, italic: false, alignment: 'left', inline: false, padding: { top: 0, right: 0, bottom: 0, left: 0 } }] }] }],
};

export function newId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function variableToken(key) {
    return `{{${key}}}`;
}

/** Replace known variables only; unknown tokens stay visible so configuration errors are obvious. */
export function mergeText(value, variables) {
    return String(value || '').replace(/\{\{([a-z][a-z0-9_]*)\}\}/g, (token, key) => Object.prototype.hasOwnProperty.call(variables, key) ? variables[key] : token);
}

function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
}

function safeUrl(value) {
    const url = String(value || '').trim();
    return /^https:\/\//i.test(url) || /^mailto:/i.test(url) || /^tel:/i.test(url) ? url : '';
}

function safeImageUrl(value) {
    const url = String(value || '').trim();
    return /^https:\/\//i.test(url) ? url : '';
}

function blockAlignment(block) {
    return ['left', 'center', 'right'].includes(block.alignment) ? block.alignment : 'left';
}

function blockPadding(block) {
    const padding = block.padding || {};
    return ['top', 'right', 'bottom', 'left'].map(side => `${Number(padding[side]) || 0}px`).join(' ');
}

/** Render one block; inline placement is controlled by its full-width group. */
function blockHtml(block, variables, inline = false) {
    const display = inline ? 'inline-block' : 'block';
    const spacing = inline ? '8px' : '0';
    if (block.type === 'image') {
        const src = safeImageUrl(mergeText(block.url, variables));
        if (!src) return '';
        const image = `<img src="${escapeHtml(src)}" alt="${escapeHtml(block.alt || '')}" width="${Number(block.width) || 120}" style="display:block;max-width:100%;height:auto;border:0;outline:0;text-decoration:none;">`;
        const link = safeUrl(mergeText(block.link, variables));
        return `<div style="display:${display};vertical-align:top;box-sizing:border-box;margin:0 ${spacing} 0 0;padding:${blockPadding(block)};border:0;">${link ? `<a href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;line-height:0;border:0;outline:0;text-decoration:none;">${image}</a>` : image}</div>`;
    }
    if (block.type === 'spacer') return `<div style="display:block;box-sizing:content-box;height:${Number(block.height) || 8}px;line-height:${Number(block.height) || 8}px;padding:${blockPadding(block)};">&nbsp;</div>`;
    const content = escapeHtml(mergeText(block.value, variables)).replace(/\n/g, '<br>');
    const styled = `<span style="font-family:Verdana,Geneva,sans-serif;font-size:${Number(block.fontSize) || 14}px;color:${escapeHtml(block.color || '#706f6f')};font-weight:${block.bold ? '700' : '400'};font-style:${block.italic ? 'italic' : 'normal'};">${content}</span>`;
    const link = safeUrl(mergeText(block.link, variables));
    return `<div style="display:${display};vertical-align:top;box-sizing:border-box;margin:0 ${spacing} 0 0;padding:${blockPadding(block)};">${link ? `<a href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">${styled}</a>` : styled}</div>`;
}

/** Group adjacent inline items so alignment applies to their complete shared row. */
function columnHtml(blocks, variables) {
    const parts = [];
    for (let index = 0; index < blocks.length;) {
        const block = blocks[index];
        if (block.inline === true && block.type !== 'spacer') {
            const alignment = blockAlignment(block);
            const group = [];
            while (index < blocks.length && blocks[index].inline === true && blocks[index].type !== 'spacer' && blockAlignment(blocks[index]) === alignment) {
                group.push(blockHtml(blocks[index], variables, true));
                index += 1;
            }
            parts.push(`<div style="display:block;width:100%;text-align:${alignment};border:0;">${group.join('')}</div>`);
        } else {
            parts.push(`<div style="display:block;width:100%;text-align:${blockAlignment(block)};border:0;">${blockHtml(block, variables)}</div>`);
            index += 1;
        }
    }
    return parts.join('');
}

/** Produce compact table-based HTML that pastes reliably into common email clients. */
export function signatureHtml(template, variables) {
    const tableColumnCount = (template?.rows || []).some(row => row.layout === 'two') ? 2 : 1;
    const rows = (template?.rows || []).map(row => {
        const cells = (row.columns || []).map((column, index) => `<td${row.layout === 'single' && tableColumnCount > 1 ? ` colspan="${tableColumnCount}"` : ''} valign="top" style="width:auto;padding:4px ${index < row.columns.length - 1 ? '12px' : '0'} 4px 0;border:0;outline:0;text-align:left;">${columnHtml(column.blocks || [], variables)}</td>`).join('');
        return `<tr style="border:0;outline:0;">${cells}</tr>`;
    }).join('');
    return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:auto;border-collapse:collapse;border:0;outline:0;font-family:Verdana,Geneva,sans-serif;"><tbody style="border:0;outline:0;">${rows}</tbody></table>`;
}
